/**
 * TanStack Query hooks for Articles
 *
 * This file provides React Query hooks for fetching and mutating article data.
 * These hooks replace the async methods in the Zustand articles store.
 *
 * Cross-store synchronization:
 * - getById updates articles-analysis and articles-funnels caches
 * - getByOrgId and getByBrandId update articles-funnels cache
 * - After successful create, one-click generate, or content refresh: markFreeArticleCreated on billing cache and invalidate billing for that org (credits/plan refetch)
 * - update updates articles-analysis cache when analysis is returned
 *
 * @module articles-queries
 */
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type Content, type ContentCreate, type ContentStatus } from '../../entity/api/content';
import type {
  ArticleSortAndFilter,
  GenerateOneClickReq,
  RefreshContentReq,
} from '../../entity/api/routes/article';
import { type SeoResponse } from '../../entity/api/seo';
import {
  createArticle,
  exportArticleById,
  generateOneClick,
  getArticleById,
  getArticlesByBrandId,
  getArticlesByOrgId,
  refreshContent,
  removeArticleById,
  updateArticleById,
} from '../../sdk/article';
import { articlesAnalysisKeys } from './articles-analysis-queries';
import { articlesFunnelsKeys } from './articles-funnels-queries';
import { prefetchUsersByIds } from './users-queries';

// ============ TYPES ============

export type ArticleWithSeo = Content & {
  seo: SeoResponse;
};

// ============ QUERY KEYS ============

export const articlesKeys = {
  all: ['articles'] as const,
  byId: function (id: string) {
    return [...articlesKeys.all, id] as const;
  },
  byOrgId: function (orgId: string, queryParams?: ArticleSortAndFilter) {
    // Include query params in key for proper cache separation
    return queryParams != null
      ? ([...articlesKeys.all, 'org', orgId, queryParams] as const)
      : ([...articlesKeys.all, 'org', orgId] as const);
  },
  byBrandId: function (brandId: string, queryParams?: ArticleSortAndFilter) {
    // Include query params in key for proper cache separation
    return queryParams != null
      ? ([...articlesKeys.all, 'brand', brandId, queryParams] as const)
      : ([...articlesKeys.all, 'brand', brandId] as const);
  },
};

// ============ QUERIES ============

/**
 * Fetch a single article by ID
 * Also populates articles-analysis and articles-funnels caches
 *
 * Replaces: getStores().articles.getState().getById(id)
 */
export function useArticleById(id: string | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: articlesKeys.byId(id ?? ''),
    queryFn: async function () {
      if (id == null) {
        throw new Error('Article ID is required');
      }
      const response = await getArticleById(id);
      const { content, funnel, analysis, seoScore } = response.data;

      // Cache the article with SEO score
      const articleWithSeo: ArticleWithSeo = {
        ...content,
        seo: seoScore,
      };

      // Handle analysis - cache or remove
      if (analysis != null) {
        queryClient.setQueryData(articlesAnalysisKeys.byArticleId(id), analysis);
      } else {
        queryClient.removeQueries({
          queryKey: articlesAnalysisKeys.byArticleId(id),
        });
      }

      // Handle funnel - cache or remove
      if (funnel != null) {
        queryClient.setQueryData(articlesFunnelsKeys.byArticleId(id), funnel);
      } else {
        queryClient.removeQueries({
          queryKey: articlesFunnelsKeys.byArticleId(id),
        });
      }

      return articleWithSeo;
    },
    enabled: id != null && id !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch articles by organization ID
 * Also populates articles-funnels cache
 *
 * Replaces: getStores().articles.getState().getByOrgId(organizationId)
 */
export function useArticlesByOrgId(
  organizationId: string | undefined | null,
  queryParams?: ArticleSortAndFilter
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: articlesKeys.byOrgId(organizationId ?? '', queryParams),
    queryFn: async function () {
      if (organizationId == null) {
        throw new Error('Organization ID is required');
      }
      const response = await getArticlesByOrgId(organizationId, queryParams);
      const { contents, funnels } = response.data;
      const { totalCount } = response;

      // Transform contents to include default SEO
      const articlesWithSeo: ArticleWithSeo[] = contents.map(function (content) {
        return {
          ...content,
          seo: { total: 0, result: [] },
        };
      });

      // Cache each article individually
      for (const article of articlesWithSeo) {
        queryClient.setQueryData(articlesKeys.byId(article._id), article);
      }

      // Cache funnels by their article ID
      for (const funnel of funnels) {
        queryClient.setQueryData(articlesFunnelsKeys.byArticleId(funnel.contentId), funnel);
      }

      // Prefetch users for "created by" display
      const userIds = contents.map((c) => c.createdBy).filter(Boolean);
      await prefetchUsersByIds(queryClient, userIds);

      return {
        articles: articlesWithSeo,
        totalCount,
      };
    },
    enabled: organizationId != null && organizationId !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch articles by brand ID
 * Also populates articles-funnels cache
 *
 * Replaces: getStores().articles.getState().getByBrandId(brandId)
 */
export function useArticlesByBrandId(
  brandId: string | undefined | null,
  queryParams?: ArticleSortAndFilter
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: articlesKeys.byBrandId(brandId ?? '', queryParams),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      const response = await getArticlesByBrandId(brandId, queryParams);
      const { contents, funnels } = response.data;
      const totalCount = response.totalCount ?? 0;

      // Transform contents to include default SEO
      const articlesWithSeo: ArticleWithSeo[] = contents.map(function (content) {
        return {
          ...content,
          seo: { total: 0, result: [] },
        };
      });

      // Cache each article individually
      for (const article of articlesWithSeo) {
        queryClient.setQueryData(articlesKeys.byId(article._id), article);
      }

      // Cache funnels by their article ID
      for (const funnel of funnels) {
        queryClient.setQueryData(articlesFunnelsKeys.byArticleId(funnel.contentId), funnel);
      }

      // Prefetch users for "created by" display
      const userIds = contents.map((c) => c.createdBy).filter(Boolean);
      await prefetchUsersByIds(queryClient, userIds);

      return {
        articles: articlesWithSeo,
        totalCount,
      };
    },
    enabled: brandId != null && brandId !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============ MUTATIONS ============

/**
 * Create a new article.
 * On success, updates organization billing cache (hasFreeArticleCreated) and invalidates billing so plan/credits refetch.
 *
 * Replaces: getStores().articles.getState().create(data)
 */
export function useCreateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: ContentCreate) {
      const response = await createArticle(data);
      return response.data;
    },
    onSuccess: async function (data) {
      const articleWithSeo: ArticleWithSeo = {
        ...data,
        seo: { total: 0, result: [] },
      };

      // Cache the new article
      queryClient.setQueryData(articlesKeys.byId(data._id), articleWithSeo);

      // Invalidate org and brand lists to include the new article
      void queryClient.invalidateQueries({
        queryKey: articlesKeys.byOrgId(data.organizationId),
      });
      void queryClient.invalidateQueries({
        queryKey: articlesKeys.byBrandId(data.brandId),
      });

      await import('./organization-billing-queries')
        .then(function ({ markFreeArticleCreated, invalidateOrganizationBilling }) {
          // we invalidate so we can sync the plan credits
          invalidateOrganizationBilling(queryClient, data.organizationId);
          markFreeArticleCreated(queryClient, data.organizationId);
        })
        .catch(function () {
          // organization-billing-queries might not exist yet during migration
        });
    },
  });
}

/**
 * Generate an article in one click.
 * On success, updates organization billing cache (hasFreeArticleCreated) and invalidates billing so plan/credits refetch.
 *
 * Replaces: getStores().articles.getState().oneClickGenerate(data)
 */
export function useOneClickGenerate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: GenerateOneClickReq['body']) {
      const response = await generateOneClick(data);
      return response.data;
    },
    onSuccess: async function (data) {
      const articleWithSeo: ArticleWithSeo = {
        ...data.content,
        seo: { total: 0, result: [] },
      };

      // Cache the new article
      queryClient.setQueryData(articlesKeys.byId(data.content._id), articleWithSeo);

      // Invalidate org and brand lists
      void queryClient.invalidateQueries({
        queryKey: articlesKeys.byOrgId(data.content.organizationId),
      });
      void queryClient.invalidateQueries({
        queryKey: articlesKeys.byBrandId(data.content.brandId),
      });

      await import('./organization-billing-queries')
        .then(function ({ markFreeArticleCreated, invalidateOrganizationBilling }) {
          // we invalidate so we can sync the plan credits
          invalidateOrganizationBilling(queryClient, data.content.organizationId);
          markFreeArticleCreated(queryClient, data.content.organizationId);
        })
        .catch(function () {
          // organization-billing-queries might not exist yet during migration
        });
    },
  });
}

/**
 * Update an article
 * Also updates articles-analysis cache if analysis is returned
 *
 * Replaces: getStores().articles.getState().update(id, data)
 */
export function useUpdateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, data }: { id: string; data: Partial<ArticleWithSeo> }) {
      const response = await updateArticleById(id, data);
      return { response: response.data, id, inputData: data };
    },
    onSuccess: function ({ response, id, inputData }) {
      const { analysis, seoScore, content } = response;

      // Get existing article to merge
      const existingArticle = queryClient.getQueryData<ArticleWithSeo>(articlesKeys.byId(id));

      // Update the article cache
      const updatedArticle: ArticleWithSeo = {
        ...(existingArticle ?? {}),
        ...inputData,
        seo: seoScore,
        wordCount: content.wordCount,
      } as ArticleWithSeo;

      queryClient.setQueryData(articlesKeys.byId(id), updatedArticle);

      // Update analysis cache if returned
      if (analysis != null) {
        queryClient.setQueryData(articlesAnalysisKeys.byArticleId(id), analysis);
      }

      // Invalidate list queries to refetch with updated data
      // Note: With pagination, we can't reliably update all cache entries,
      // so we invalidate and let them refetch
      if (existingArticle != null) {
        void queryClient.invalidateQueries({
          queryKey: articlesKeys.byOrgId(existingArticle.organizationId),
        });
        void queryClient.invalidateQueries({
          queryKey: articlesKeys.byBrandId(existingArticle.brandId),
        });
      }
    },
  });
}

/**
 * Refresh or rewrite article content via the API.
 * On success, updates organization billing cache (hasFreeArticleCreated) and invalidates billing so plan/credits refetch.
 *
 * Replaces: getStores().articles.getState().refresh(data)
 */
export function useRefreshArticle(brandId: string, organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: RefreshContentReq['body']) {
      const response = await refreshContent(data);
      return response; // Returns { contentId: string }
    },
    onSuccess: async function () {
      if (brandId != null) {
        // Invalidate article list by brandId to show updated article
        void queryClient.invalidateQueries({
          queryKey: articlesKeys.byBrandId(brandId),
        });
      }

      await import('./organization-billing-queries')
        .then(function ({ markFreeArticleCreated, invalidateOrganizationBilling }) {
          // we invalidate so we can sync the plan credits
          invalidateOrganizationBilling(queryClient, organizationId);
          markFreeArticleCreated(queryClient, organizationId);
        })
        .catch(function () {
          // organization-billing-queries might not exist yet during migration
        });
    },
  });
}

/**
 * Remove an article
 *
 * Replaces: getStores().articles.getState().remove(id)
 */
export function useRemoveArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (id: string) {
      await removeArticleById(id);
      return id;
    },
    onSuccess: function (id) {
      // Get article before removing to know which lists to update
      const article = queryClient.getQueryData<ArticleWithSeo>(articlesKeys.byId(id));

      // Remove from cache
      queryClient.removeQueries({ queryKey: articlesKeys.byId(id) });

      // Remove related analysis and funnel
      queryClient.removeQueries({
        queryKey: articlesAnalysisKeys.byArticleId(id),
      });
      queryClient.removeQueries({
        queryKey: articlesFunnelsKeys.byArticleId(id),
      });

      // Invalidate list queries to refetch with updated data
      // Note: With pagination, we can't reliably update all cache entries,
      // so we invalidate and let them refetch
      if (article != null) {
        void queryClient.invalidateQueries({
          queryKey: articlesKeys.byOrgId(article.organizationId),
        });
        void queryClient.invalidateQueries({
          queryKey: articlesKeys.byBrandId(article.brandId),
        });
      }
    },
  });
}

/**
 * Export an article
 *
 * Replaces: getStores().articles.getState().exportById(id, primaryKeyword, kind, isDownload)
 */
export function useExportArticle() {
  return useMutation({
    mutationFn: async function ({
      id,
      primaryKeyword,
      kind,
      isDownload,
    }: {
      id: string;
      primaryKeyword: string;
      kind: Parameters<typeof exportArticleById>[2];
      isDownload: boolean;
    }) {
      return exportArticleById(id, primaryKeyword, kind, isDownload);
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get article from cache by ID
 */
export function getArticleFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): ArticleWithSeo | undefined {
  return queryClient.getQueryData<ArticleWithSeo>(articlesKeys.byId(id));
}

/**
 * Get articles by brand ID from cache (base query without params)
 */
export function getArticlesByBrandIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  brandId: string,
  queryParams?: ArticleSortAndFilter
): { articles: ArticleWithSeo[]; totalCount: number } | undefined {
  return queryClient.getQueryData<{ articles: ArticleWithSeo[]; totalCount: number }>(
    articlesKeys.byBrandId(brandId, queryParams)
  );
}

/**
 * Get articles by org ID from cache (base query without params)
 */
export function getArticlesByOrgIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  queryParams?: ArticleSortAndFilter
): { articles: ArticleWithSeo[]; totalCount: number } | undefined {
  return queryClient.getQueryData<{ articles: ArticleWithSeo[]; totalCount: number }>(
    articlesKeys.byOrgId(orgId, queryParams)
  );
}

/**
 * Update article status in cache (sync operation)
 * Replaces: getStores().articles.getState().updateStatusToStore(id, status)
 */
export function updateArticleStatusInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  status: ContentStatus
): ArticleWithSeo | undefined {
  const article = queryClient.getQueryData<ArticleWithSeo>(articlesKeys.byId(id));
  if (article == null) {
    return undefined;
  }

  const updated = { ...article, status };
  queryClient.setQueryData(articlesKeys.byId(id), updated);

  // Invalidate list queries to refetch with updated data
  // Note: With pagination, we can't reliably update all cache entries,
  // so we invalidate and let them refetch
  void queryClient.invalidateQueries({
    queryKey: articlesKeys.byOrgId(article.organizationId),
  });
  void queryClient.invalidateQueries({
    queryKey: articlesKeys.byBrandId(article.brandId),
  });

  return updated;
}

/**
 * Invalidate all article queries
 */
export function invalidateArticles(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: articlesKeys.all });
}

/**
 * Invalidate a specific article query
 */
export function invalidateArticleById(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): void {
  void queryClient.invalidateQueries({ queryKey: articlesKeys.byId(id) });
}
// ============ IMPERATIVE FETCH FUNCTIONS ============

/**
 * Fetch articles by brand ID and cache them (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand stores for backward compatibility.
 */
export async function fetchArticlesByBrandIdImperative(
  queryClient: QueryClient,
  brandId: string,
  queryParams?: ArticleSortAndFilter
): Promise<{ articles: ArticleWithSeo[]; totalCount: number }> {
  const response = await getArticlesByBrandId(brandId, queryParams);
  const { contents, funnels } = response.data;
  const totalCount = response.totalCount ?? 0;

  // Transform contents to include default SEO
  const articlesWithSeo: ArticleWithSeo[] = contents.map(function (content) {
    return {
      ...content,
      seo: { total: 0, result: [] },
    };
  });

  // Cache in TanStack Query - brand list with totalCount
  // Use base key (without params) for imperative fetches
  queryClient.setQueryData(articlesKeys.byBrandId(brandId, queryParams), {
    articles: articlesWithSeo,
    totalCount,
  });

  // Cache each article individually
  for (const article of articlesWithSeo) {
    queryClient.setQueryData(articlesKeys.byId(article._id), article);
  }

  // Cache funnels by their article ID
  for (const funnel of funnels) {
    queryClient.setQueryData(articlesFunnelsKeys.byArticleId(funnel.contentId), funnel);
  }

  // Prefetch users for "created by" display
  const userIds = contents.map((c) => c.createdBy).filter(Boolean);
  await prefetchUsersByIds(queryClient, userIds);

  return { articles: articlesWithSeo, totalCount };
}

/**
 * Fetch articles by organization ID and cache them (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand stores for backward compatibility.
 */
export async function fetchArticlesByOrgIdImperative(
  queryClient: QueryClient,
  organizationId: string,
  queryParams?: ArticleSortAndFilter
): Promise<{ articles: ArticleWithSeo[]; totalCount: number }> {
  const response = await getArticlesByOrgId(organizationId, queryParams);
  const { contents, funnels } = response.data;
  const { totalCount } = response;

  // Transform contents to include default SEO
  const articlesWithSeo: ArticleWithSeo[] = contents.map(function (content) {
    return {
      ...content,
      seo: { total: 0, result: [] },
    };
  });

  // Cache in TanStack Query - org list with totalCount
  // Use base key (without params) for imperative fetches
  queryClient.setQueryData(articlesKeys.byOrgId(organizationId, queryParams), {
    articles: articlesWithSeo,
    totalCount,
  });

  // Cache each article individually
  for (const article of articlesWithSeo) {
    queryClient.setQueryData(articlesKeys.byId(article._id), article);
  }

  // Cache funnels by their article ID
  for (const funnel of funnels) {
    queryClient.setQueryData(articlesFunnelsKeys.byArticleId(funnel.contentId), funnel);
  }

  // Prefetch users for "created by" display
  const userIds = contents.map((c) => c.createdBy).filter(Boolean);
  await prefetchUsersByIds(queryClient, userIds);

  return { articles: articlesWithSeo, totalCount };
}

/**
 * Fetch a single article by ID and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand stores for backward compatibility.
 */
export async function fetchArticleByIdImperative(
  queryClient: QueryClient,
  id: string
): Promise<ArticleWithSeo> {
  const response = await getArticleById(id);
  const { content, funnel, analysis, seoScore } = response.data;

  // Cache the article with SEO score
  const articleWithSeo: ArticleWithSeo = {
    ...content,
    seo: seoScore,
  };

  // Cache in TanStack Query
  queryClient.setQueryData(articlesKeys.byId(id), articleWithSeo);

  // Handle analysis - cache or remove
  if (analysis != null) {
    queryClient.setQueryData(articlesAnalysisKeys.byArticleId(id), analysis);
  } else {
    queryClient.removeQueries({
      queryKey: articlesAnalysisKeys.byArticleId(id),
    });
  }

  // Handle funnel - cache or remove
  if (funnel != null) {
    queryClient.setQueryData(articlesFunnelsKeys.byArticleId(id), funnel);
  } else {
    queryClient.removeQueries({
      queryKey: articlesFunnelsKeys.byArticleId(id),
    });
  }

  return articleWithSeo;
}

/**
 * Set article in cache (for socket updates, etc.)
 * Also syncs to Zustand for backward compatibility.
 */
export function setArticleInCache(queryClient: QueryClient, article: ArticleWithSeo): void {
  queryClient.setQueryData(articlesKeys.byId(article._id), article);

  // Invalidate list queries to refetch with updated data
  // Note: With pagination, we can't reliably update all cache entries,
  // so we invalidate and let them refetch
  void queryClient.invalidateQueries({
    queryKey: articlesKeys.byOrgId(article.organizationId),
  });
  void queryClient.invalidateQueries({
    queryKey: articlesKeys.byBrandId(article.brandId),
  });
}

/**
 * Remove article from cache (for socket updates, etc.)
 * Also syncs to Zustand for backward compatibility.
 */
export function removeArticleFromCache(queryClient: QueryClient, id: string): void {
  // Get article before removing to know which lists to update
  const article = queryClient.getQueryData<ArticleWithSeo>(articlesKeys.byId(id));

  // Remove from cache
  queryClient.removeQueries({ queryKey: articlesKeys.byId(id) });

  // Remove related analysis and funnel
  queryClient.removeQueries({
    queryKey: articlesAnalysisKeys.byArticleId(id),
  });
  queryClient.removeQueries({
    queryKey: articlesFunnelsKeys.byArticleId(id),
  });

  // Invalidate list queries to refetch with updated data
  // Note: With pagination, we can't reliably update all cache entries,
  // so we invalidate and let them refetch
  if (article != null) {
    void queryClient.invalidateQueries({
      queryKey: articlesKeys.byOrgId(article.organizationId),
    });
    void queryClient.invalidateQueries({
      queryKey: articlesKeys.byBrandId(article.brandId),
    });
  }
}
